import { pipe } from "effect";
import { formatUnits } from "viem";
import { P, match } from "ts-pattern";
import { toLowerCase } from "effect/String";
import { Amount, Usd, makeAmount, makeUsd } from "@silo/types";
import {
  parseAmount,
  parseCompactNotation,
  parseNumber,
  trimNegativeZero,
} from "./string";
import { shiftDecimal } from "./bigint";

const smallFullFormatter = new Intl.NumberFormat("en-US", {
  style: "decimal",
  maximumSignificantDigits: 3,
});

const mediumFullFormatter = new Intl.NumberFormat("en-US", {
  style: "decimal",
  maximumSignificantDigits: 4,
});

const largeFullFormatter = new Intl.NumberFormat("en-US", {
  style: "decimal",
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

export function asFullAmount(amount: Amount, decimals: number): string {
  assertIsNonNegativeInteger(decimals);

  return pipe(formatUnits(amount, decimals), parseNumber, (num) => {
    const abs = Math.abs(num);

    return match(abs)
      .with(P.number.lt(0.1), () => smallFullFormatter.format(num))
      .with(P.number.lt(1000), () => mediumFullFormatter.format(num))
      .otherwise(() => largeFullFormatter.format(num));
  });
}

export function asFullAmountValue(amount: Amount, decimals: number): Amount {
  const formatted = asFullAmount(amount, decimals);

  return parseAmount(formatted, decimals);
}

const maxDigitsFormatter = new Intl.NumberFormat("en-US", {
  style: "decimal",
  maximumFractionDigits: 3,
});

const compactSignificantFormatter = new Intl.NumberFormat("en-US", {
  style: "decimal",
  minimumFractionDigits: 0,
  maximumFractionDigits: 1,
  notation: "compact",
});

export function asCompactAmount(amount: Amount, decimals: number): string {
  assertIsNonNegativeInteger(decimals);

  return pipe(
    formatUnits(amount, decimals),
    parseNumber,
    (num) =>
      match(Math.abs(num))
        .with(P.number.gte(999.95), (abs) => {
          const absValueToFormat = Math.max(abs, 1000);

          return compactSignificantFormatter.format(
            Math.sign(num) * absValueToFormat
          );
        })
        /**
         * Two formatters are used since `maximumSignificantDigits` and
         * `maximumFractionDigits` cannot currently be used together. The new
         * `roundingPriority` prop will ultimately fix this:
         * https://caniuse.com/mdn-javascript_builtins_intl_numberformat_numberformat_options_roundingpriority_parameter
         */
        .otherwise(() =>
          pipe(
            num,
            (_num) => mediumFullFormatter.format(_num),
            parseNumber,
            (_num) => maxDigitsFormatter.format(_num)
          )
        ),
    trimNegativeZero,
    toLowerCase
  );
}

export function asCompactAmountValue(amount: Amount, decimals: number): Amount {
  const formatted = asCompactAmount(amount, decimals);

  return pipe(parseCompactNotation(formatted, decimals), makeAmount);
}

function isNonNegativeInteger(num: number): boolean {
  return Number.isInteger(num) && num >= 0;
}

function assertIsNonNegativeInteger(num: number): void {
  if (!isNonNegativeInteger(num)) {
    throw new Error(`Expected ${num} to be a non-negative integer`);
  }
}

export function convertToUsd(
  amount: Amount,
  decimals: number,
  price: Usd
): Usd {
  return makeUsd(shiftDecimal(amount * price, -decimals));
}

export function convertToken(
  amount: Amount,
  decimals: number,
  price: Amount
): Amount {
  return makeAmount(shiftDecimal(amount * price, -decimals));
}
